import { apiFetch, apiFetchRaw } from './client.js';

/** Transfers of commissions to the Norvix revenue account (DEV-127). Amounts are integer cent strings. */

export type TransferMode = 'OFF' | 'CALCULATE_ONLY' | 'LIVE';
export type TransferStatus = 'CALCULATED' | 'SUBMITTED' | 'EXECUTION_UNKNOWN' | 'SETTLED' | 'FAILED' | 'SKIPPED_GUARD';

export interface CommissionTransfer {
  id: string;
  currency: string;
  kind: 'DAILY' | 'MANUAL' | 'OPENING' | 'REVERSE';
  fromAccountUuid: string;
  toAccountUuid: string;
  /** Masked (country + check digits + last 4). */
  fromIban: string;
  /** Masked (country + check digits + last 4). */
  toIban: string;
  amountCents: string;
  /** Commissions this transfer settled that GuruPay's fees had already taken (DEV-127). */
  offsetCents?: string;
  itemCount: number;
  cutoffAt: string;
  mode: 'CALCULATE_ONLY' | 'LIVE';
  status: TransferStatus;
  guardDetails: Record<string, unknown> | null;
  providerTransactionUuid: string | null;
  attempts: number;
  lastError: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommissionTransferItem {
  id: string;
  ledgerEntryId: string;
  kind: string;
  customerAccountId: string;
  provider: string;
  currency: string;
  amountCents: string;
  feeName: string;
  postedAt: string;
  status: string;
}

export interface CurrencyPosition {
  currency: string;
  mode: TransferMode;
  masterBalanceCents: string | null;
  liabilitiesCents: string | null;
  liabilitiesLedgerCents: string | null;
  liabilitiesInFlightCents: string | null;
  /** Inbound payments at GuruPay not yet credited in our ledger. */
  liabilitiesInboundCents?: string | null;
  /** Masked IBAN the sweep moves from (client-money account); null when unknown. */
  fromIban?: string | null;
  /** Masked IBAN the sweep moves to (Norvix revenue account); null when not set. */
  toIban?: string | null;
  /** What on the client-money account is Norvix's; the most a sweep moves. */
  norvixShareCents?: string | null;
  pendingCommissionsCents: string;
  inTransferCents: string;
  differenceCents: string | null;
  covered: boolean | null;
  positionError: string | null;
  lastTransfer: CommissionTransfer | null;
  nextTransferAt: string | null;
  /** Commissions already moved whose fee was voided afterwards; corrected by hand, never auto-netted. */
  voidedAfterTransfer: { count: number; totalCents: string; items: VoidedTransferredItem[] };
}

export interface VoidedTransferredItem {
  id: string;
  ledgerEntryId: string;
  transferId: string;
  amountCents: string;
  feeName: string;
  postedAt: string;
}

export interface RunResult {
  currency: string;
  outcome: string;
  netCents: string;
  itemCount: number;
  transferId: string | null;
  message: string;
}

export interface TransferPage {
  items: CommissionTransfer[];
  total: number;
  hasNext: boolean;
}

export const commissionTransfersApi = {
  position: (): Promise<{ mode: TransferMode; currencies: CurrencyPosition[] }> => apiFetch('/admin/client-money/position'),
  list: async (query: { currency?: string; from?: string; to?: string; page: number; limit: number }): Promise<TransferPage> => {
    const params = new URLSearchParams({ page: String(query.page), limit: String(query.limit) });
    if (query.currency) params.set('currency', query.currency);
    // Inclusive UTC days (YYYY-MM-DD) on the transfer's creation time.
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    const envelope = await apiFetchRaw<CommissionTransfer[]>(`/admin/commission-transfers?${params.toString()}`);
    if (envelope.error !== undefined || envelope.data === null) {
      throw new Error(envelope.error?.detail ?? 'The transfer log could not be loaded.');
    }
    const meta = envelope.meta;
    return { items: envelope.data, total: meta?.total ?? envelope.data.length, hasNext: meta?.hasNext ?? false };
  },
  items: (id: string): Promise<{ transfer: CommissionTransfer; linked: boolean; items: CommissionTransferItem[] }> =>
    apiFetch(`/admin/commission-transfers/${encodeURIComponent(id)}/items`),
  resolve: (id: string, input: { outcome: 'SETTLED' | 'FAILED'; providerTransactionUuid?: string; reason: string }): Promise<CommissionTransfer> =>
    apiFetch(`/admin/commission-transfers/${encodeURIComponent(id)}/resolve`, { method: 'POST', body: input }),
  run: (currency: string, idempotencyKey: string): Promise<RunResult> =>
    apiFetch('/admin/commission-transfers/run', { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: { currency } }),
};
